import type { EChartsInstance } from "echarts-for-react";
import {
	type ChartArea,
	type TimeRange,
	pixelToTimestamp,
} from "./coordinateConversion";

const LABEL_MARGIN = 3;

export type CursorTimeLabelOptions = {
	chart: EChartsInstance;
	label: HTMLElement;
	connector?: HTMLElement | null;
	chartArea: ChartArea;
	window: TimeRange;
	format: (time: number) => string;
};

export const followCursorTimeLabel = ({
	chart,
	label,
	connector,
	chartArea,
	window: timeWindow,
	format,
}: CursorTimeLabelOptions) => {
	const zr = chart.getZr();
	if (!zr) return;

	let frame = 0;

	const hide = () => {
		label.style.visibility = "hidden";
		if (connector) connector.style.visibility = "hidden";
	};

	const renderConnector = (offsetX: number, offsetY: number) => {
		if (!connector) return;

		const middle =
			chartArea.bottom + LABEL_MARGIN + label.offsetHeight / 2;
		const top = Math.max(chartArea.top, Math.min(offsetY, middle));

		connector.style.transform = `translate(${offsetX}px, ${top}px)`;
		connector.style.height = `${middle - top}px`;
		connector.style.visibility = "visible";
	};

	const render = (offsetX: number, offsetY: number) => {
		if (offsetX < chartArea.left || offsetX > chartArea.right) {
			hide();
			return;
		}

		label.textContent = format(
			pixelToTimestamp(offsetX, chartArea, timeWindow),
		);
		label.style.visibility = "visible";

		const width = label.offsetWidth;
		const left = Math.max(
			0,
			Math.min(offsetX - width / 2, chart.getWidth() - width),
		);

		label.style.transform = `translate(${left}px, ${chartArea.bottom + LABEL_MARGIN}px)`;

		renderConnector(offsetX, offsetY);
	};

	const handleMouseMove = (event: any) => {
		if (frame) return;

		const { offsetX, offsetY } = event;
		frame = requestAnimationFrame(() => {
			frame = 0;
			render(offsetX, offsetY);
		});
	};

	zr.on("mousemove", handleMouseMove);
	zr.on("globalout", hide);

	return () => {
		if (frame) cancelAnimationFrame(frame);
		zr.off("mousemove", handleMouseMove);
		zr.off("globalout", hide);
		hide();
	};
};
